import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import api from "../../../services/api";
import { criarOsDoAlerta } from "../context/apiAcoes";
import { ERP_ATIVO } from "../config/formTicketParams";
import useStyles from "./styles";

// Confirmação da ação "Criar OS" (OS faltante). Cria registro real no ERP,
// então o operador revisa os parâmetros antes. O formulário é por ERP:
// - HUBSOFT: listas de /interno/find/paramsNewOs, payload no shape que o
//   case HUBSOFT de createOS.execute espera (tipoOs/prioridade/agenda/
//   técnicos/disponibilidade + janela programada);
// - IXC: listas de /interno/find/paramsNewTicket (o paramsNewOs não tem case
//   IXC) + colaboradores por setor (/interno/find/funcionarios, isOs) e
//   endereço pré-carregado do cliente (/interno/find/origemEndereco), payload
//   no shape de createOsIxc — o vínculo com o ticket (idTicket) e o contrato
//   (clienteServicoId) o backend injeta em acoes/criarOs.js a partir do
//   atendimento/serviço validados, não do form.
// ERPs sem criação de OS nem chegam aqui (ação desabilitada em
// alertasInteligentes + 400 do backend via capacidadesErp.js).

const DURACAO_PADRAO_HORAS = 2;

function descricaoPadrao(incidente) {
	const alerta = incidente?.alerta || {};
	return [
		"OS gerada pela Central de Alertas: atendimento resolvido sem ordem de serviço vinculada.",
		"",
		`Equipamento: ${incidente?.equipamento?.nome || alerta.hostId || "—"}`,
		`Cliente: ${incidente?.cliente?.nome || "—"}`,
		`Alerta: ${alerta.titulo || "—"}`,
		`Atendimento: ${incidente?.atendimento?.id || "—"}`,
	].join("\n");
}

// `erp`/`erpId`: ERP do incidente (o escolhido no painel) — listas e criação
// da OS saem dele. Sem eles, a variável do front (como antes).
export default function ModalCriarOs({
	aberto,
	incidente,
	onFechar,
	onSucesso,
	erp: erpProp,
	erpId,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const erp = erpProp || ERP_ATIVO();
	const comErp = (corpo) => ({ ...corpo, erpId: erpId || undefined });
	const [listas, setListas] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	const [enviando, setEnviando] = useState(false);
	const [form, setForm] = useState({
		// HUBSOFT
		tipoOs: null,
		prioridade: null,
		agenda: null,
		tecnicos: [],
		disponibilidade: [],
		// IXC
		assunto: null,
		filial: null,
		setor: null,
		responsavel: null,
		prioridadeIxc: null,
		endereco: "",
		// comum
		descricao: "",
	});
	// IXC: colaboradores dependem do setor escolhido (mesmo fluxo do
	// editTicketComponentIxc — POST /interno/find/funcionarios por setor).
	const [funcionarios, setFuncionarios] = useState([]);
	const [carregandoFuncionarios, setCarregandoFuncionarios] = useState(false);

	const atendimentoId = incidente?.atendimento?.id;
	const servicoId = incidente?.alerta?.servicoId;
	const clienteErpId = incidente?.alerta?.clienteErpId;

	useEffect(() => {
		if (!aberto) return undefined;
		let ativo = true;
		setErro("");
		setEnviando(false);
		setFuncionarios([]);
		setForm((atual) => ({
			...atual,
			tipoOs: null,
			prioridade: null,
			agenda: null,
			tecnicos: [],
			disponibilidade: [],
			assunto: null,
			filial: null,
			setor: null,
			responsavel: null,
			prioridadeIxc: null,
			endereco: "",
			descricao: descricaoPadrao(incidente),
		}));

		(async () => {
			try {
				setCarregando(true);
				if (erp === "IXC") {
					// paramsNewTicket é a rota que serve as listas do IXC
					// (filiais/assuntos/setores/prioridadesOsIxc).
					const [{ data }, endereco] = await Promise.all([
						api.post("/interno/find/paramsNewTicket", comErp({})),
						clienteErpId
							? api
									.post(
										"/interno/find/origemEndereco",
										comErp({
											enderecoOrigem: "C",
											clienteErpId,
											serviceId: servicoId,
										}),
									)
									.then((r) => r?.data || "")
									.catch(() => "")
							: Promise.resolve(""),
					]);
					if (ativo) {
						setListas(data || {});
						setForm((atual) => ({ ...atual, endereco: endereco || "" }));
					}
				} else {
					const { data } = await api.post(
						"/interno/find/paramsNewOs",
						comErp({}),
					);
					if (ativo) setListas(data || {});
				}
			} catch (_) {
				if (ativo) setErro("Falha ao carregar as listas de OS do ERP.");
			} finally {
				if (ativo) setCarregando(false);
			}
		})();

		return () => {
			ativo = false;
		};
	}, [aberto, incidente]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (erp !== "IXC" || !form.setor?.id) return undefined;
		let ativo = true;
		(async () => {
			try {
				setCarregandoFuncionarios(true);
				const { data } = await api.post(
					"/interno/find/funcionarios",
					comErp({ idSetor: form.setor.id, isOs: true }),
				);
				if (ativo) setFuncionarios(data || []);
			} catch (_) {
				if (ativo) setFuncionarios([]);
			} finally {
				if (ativo) setCarregandoFuncionarios(false);
			}
		})();
		return () => {
			ativo = false;
		};
	}, [erp, form.setor]);

	// Janela programada: agora -> +2h, no formato que o ERP espera (HUBSOFT).
	const janela = useMemo(() => {
		const inicio = dayjs();
		const fim = inicio.add(DURACAO_PADRAO_HORAS, "hour");
		return {
			data_inicio_programado: inicio.format("YYYY-MM-DD"),
			hora_inicio_programado: inicio.format("HH:mm:ss"),
			data_termino_programado: fim.format("YYYY-MM-DD"),
			hora_termino_programado: fim.format("HH:mm:ss"),
		};
	}, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

	const podeEnviar =
		atendimentoId &&
		servicoId &&
		(erp === "IXC"
			? form.assunto &&
				form.filial &&
				form.setor &&
				form.responsavel &&
				form.prioridadeIxc &&
				form.endereco?.trim() &&
				clienteErpId
			: form.tipoOs &&
				form.prioridade &&
				form.agenda &&
				form.tecnicos.length > 0 &&
				form.disponibilidade.length > 0);

	const montarDataOs = () => {
		if (erp === "IXC") {
			// Shape de createOsIxc (o mesmo do modal de OS do suporte);
			// statusId "A" (Aberta) e horário "Q" (Qualquer) são os defaults do
			// sistema para OS nova. idTicket/clienteServicoId são injetados no
			// backend a partir do atendimento/serviço validados.
			return {
				clienteErpId,
				assuntoId: form.assunto?.id,
				filialId: form.filial?.id,
				departamentoId: form.setor?.id,
				responsavelId: form.responsavel?.id,
				prioridadeId: form.prioridadeIxc?.id,
				statusId: "A",
				horarioId: "Q",
				origem_endereco: "M",
				endereco: form.endereco,
				descricaoAbertura: form.descricao,
			};
		}
		return {
			statusOs: listas?.statusOs?.[0] || "pendente",
			tipoOs: { id: form.tipoOs?.id, descricao: form.tipoOs?.descricao },
			prioridade: {
				id_prioridade: form.prioridade?.id_prioridade ?? form.prioridade?.id,
				prefixo: form.prioridade?.prefixo,
				descricao: form.prioridade?.descricao,
			},
			agenda: { id: form.agenda?.id, descricao: form.agenda?.descricao },
			tecnicos: form.tecnicos,
			disponibilidade: form.disponibilidade,
			descricaoAbertura: form.descricao,
			...janela,
		};
	};

	const handleConfirmar = async () => {
		setEnviando(true);
		setErro("");
		try {
			const os = await criarOsDoAlerta({
				erpId,
				atendimentoId,
				servicoId,
				tipoAgendamento: "manual",
				dataOs: montarDataOs(),
			});
			onSucesso?.(os);
		} catch (e) {
			setErro(
				e?.response?.data?.message || "Não foi possível criar a OS no ERP.",
			);
		} finally {
			setEnviando(false);
		}
	};

	const seletor = (rotulo, campo, opcoes, obterRotulo, multiplo = false) => (
		<Autocomplete
			multiple={multiplo}
			size="small"
			options={opcoes || []}
			value={form[campo]}
			getOptionLabel={(o) => obterRotulo(o) || ""}
			isOptionEqualToValue={(a, b) =>
				(a?.id ?? a?.id_prioridade) === (b?.id ?? b?.id_prioridade)
			}
			onChange={(_, v) => setForm((atual) => ({ ...atual, [campo]: v }))}
			renderInput={(params) => <TextField {...params} label={rotulo} />}
		/>
	);

	const camposHubsoft = (
		<>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{seletor("Tipo de OS", "tipoOs", listas?.tipoOs, (o) => o?.descricao)}
				{seletor(
					"Prioridade",
					"prioridade",
					listas?.prioridade,
					(o) => o?.descricao,
				)}
			</Stack>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{seletor("Agenda", "agenda", listas?.agenda, (o) => o?.descricao)}
				{seletor(
					"Disponibilidade",
					"disponibilidade",
					listas?.disponibilidade,
					(o) => o?.descricao,
					true,
				)}
			</Stack>
			{seletor(
				"Técnicos",
				"tecnicos",
				listas?.tecnicos,
				(o) => o?.name || o?.nome,
				true,
			)}
		</>
	);

	const camposIxc = (
		<>
			{!clienteErpId && (
				<Alert severity="error">
					Este alerta não tem cliente do ERP vinculado — não é possível criar a
					OS.
				</Alert>
			)}
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{seletor("Assunto", "assunto", listas?.assuntos, (o) => o?.assunto)}
				{seletor(
					"Filial",
					"filial",
					listas?.filiais,
					(o) => (o ? `${o?.id} - ${o?.razao}` : ""),
				)}
			</Stack>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{seletor("Setor", "setor", listas?.setores, (o) => o?.setor)}
				<Box sx={{ position: "relative", width: "100%" }}>
					<Autocomplete
						size="small"
						options={funcionarios || []}
						value={form.responsavel}
						disabled={!form.setor || carregandoFuncionarios}
						getOptionLabel={(o) => o?.funcionario || ""}
						isOptionEqualToValue={(a, b) => a?.id === b?.id}
						onChange={(_, v) =>
							setForm((atual) => ({ ...atual, responsavel: v }))
						}
						renderInput={(params) => (
							<TextField {...params} label="Colaborador responsável" />
						)}
					/>
				</Box>
			</Stack>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{seletor(
					"Prioridade",
					"prioridadeIxc",
					listas?.prioridadesOsIxc,
					(o) => o?.prioridade,
				)}
				<TextField
					fullWidth
					label="Endereço"
					size="small"
					value={form.endereco}
					onChange={(e) =>
						setForm((atual) => ({ ...atual, endereco: e.target.value }))
					}
				/>
			</Stack>
		</>
	);

	return (
		<Dialog
			open={aberto}
			onClose={enviando ? undefined : onFechar}
			fullWidth
			maxWidth="sm"
		>
			<DialogTitle sx={styles.titulo}>
				<Typography variant="mySubtitle">Criar ordem de serviço</Typography>
				<Typography variant="caption" sx={styles.subtitulo}>
					{`${incidente?.equipamento?.nome || "—"} · ${incidente?.cliente?.nome || "—"}`}
				</Typography>
			</DialogTitle>
			<DialogContent>
				{carregando ? (
					<Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
						<CircularProgress size={20} />
						<Typography variant="body2" color="text.secondary">
							Carregando listas do ERP...
						</Typography>
					</Stack>
				) : (
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Alert severity="info" variant="outlined">
							Cria uma OS real no ERP, vinculada ao atendimento{" "}
							{atendimentoId ? `#${atendimentoId}` : "—"}. O monitoramento
							(Zabbix) não é alterado.
						</Alert>

						{!atendimentoId && (
							<Alert severity="error">
								Este incidente não tem atendimento vinculado — a OS precisa de um
								atendimento de origem.
							</Alert>
						)}

						<Box sx={styles.resumoAlerta}>
							<Typography variant="caption" sx={styles.rotuloResumo}>
								Alerta de origem
							</Typography>
							<Typography variant="body2">
								{incidente?.alerta?.titulo || "—"}
							</Typography>
						</Box>

						{erp === "IXC" ? camposIxc : camposHubsoft}

						<TextField
							label="Descrição do serviço"
							multiline
							minRows={4}
							size="small"
							value={form.descricao}
							onChange={(e) =>
								setForm((atual) => ({ ...atual, descricao: e.target.value }))
							}
						/>

						{erp === "IXC" ? (
							<Typography variant="caption" color="text.secondary">
								A OS entra como Aberta, melhor horário "Qualquer", vinculada ao
								atendimento do incidente.
							</Typography>
						) : (
							<Typography variant="caption" color="text.secondary">
								{`Programada para ${dayjs(janela.data_inicio_programado).format("DD/MM/YYYY")} das ${janela.hora_inicio_programado.slice(0, 5)} às ${janela.hora_termino_programado.slice(0, 5)}.`}
							</Typography>
						)}

						{erro && <Alert severity="error">{erro}</Alert>}
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onFechar} disabled={enviando}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleConfirmar}
					disabled={enviando || !podeEnviar}
				>
					{enviando ? "Criando..." : "Criar OS"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
